import { action, makeObservable, observable } from 'mobx';
import i18n from '../i18n';

export interface ConfirmDialogConfig {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

export class ConfirmDialogStore {
  @observable isOpen: boolean = false;
  @observable config: ConfirmDialogConfig | null = null;

  constructor() {
    makeObservable(this);
  }

  /**
   * Show confirmation dialog with the specified configuration
   */
  @action
  show(config: ConfirmDialogConfig): void {
    this.config = {
      confirmText: i18n.t('common.confirm'),
      cancelText: i18n.t('common.cancel'),
      variant: 'info',
      ...config,
    };
    this.isOpen = true;
  }

  /**
   * Close the confirmation dialog
   */
  @action
  close(): void {
    this.isOpen = false;
    this.config = null;
  }

  /**
   * Handle confirm action
   */
  @action
  async handleConfirm(): Promise<void> {
    if (this.config?.onConfirm) {
      try {
        await this.config.onConfirm();
      } catch (error) {
        console.error('Error in confirm action:', error);
      }
    }
    this.close();
  }

  /**
   * Handle cancel action
   */
  @action
  handleCancel(): void {
    if (this.config?.onCancel) {
      try {
        this.config.onCancel();
      } catch (error) {
        console.error('Error in cancel action:', error);
      }
    }
    this.close();
  }

  /**
   * Convenience method for showing cancellation confirmation
   */
  @action
  showCancellationConfirmation(
    message: string,
    onConfirm: () => void | Promise<void>,
    variant: 'danger' | 'warning' | 'info' = 'warning'
  ): void {
    this.show({
      title: i18n.t('common.confirmCancellation'),
      message,
      variant,
      onConfirm,
    });
  }

  /**
   * Convenience method for showing deletion confirmation
   */
  @action
  showDeletionConfirmation(
    message: string,
    onConfirm: () => void | Promise<void>
  ): void {
    this.show({
      title: i18n.t('common.confirmDeletion'),
      message,
      variant: 'danger',
      confirmText: i18n.t('common.delete'),
      onConfirm,
    });
  }
}

// Export singleton instance
export const confirmDialogStore = new ConfirmDialogStore();
